/**
 * Comparator V2.5 feedback → PostHog Surveys.
 *
 * Sends `survey sent` events in PostHog's Survey response format — the
 * `$survey_id` + `$survey_response_<question_id>` properties are what makes
 * the data appear under the Surveys product (not just under raw Events).
 * The survey itself is created in PostHog as an "api" type, meaning PostHog
 * does NOT render any native UI — our `ComparatorFeedback.vue` widget is
 * the only entry point.
 *
 * Goes through the shared posthog-js SDK initialised by `posthog.ts` on
 * advisor calculator routes (autocapture + identification handled there).
 */
import posthog from 'posthog-js';
import processProxy from '@/env/processProxy';
import { hasPosthogApiKey } from '@/plugins/posthog';

// --- PostHog Survey identifiers ------------------------------------------
// Created on EU PostHog (project 136049) as a draft "api" survey:
//   https://eu.posthog.com/project/136049/surveys/019e2bfd-62bb-0000-555d-9216db35f608
// To target a different survey in a different env (e.g. a prod-specific
// copy), set VUE_APP_COMPARATOR_FEEDBACK_SURVEY_ID and the corresponding
// question-id env vars. Defaults are the dev/staging survey above.
const SURVEY_ID = processProxy.VUE_APP_COMPARATOR_FEEDBACK_SURVEY_ID?.trim()
  || '019e2bfd-62bb-0000-555d-9216db35f608';
const SURVEY_NAME = 'Porównywarka V2.5 — feedback';
const RATING_QUESTION_ID = processProxy.VUE_APP_COMPARATOR_FEEDBACK_RATING_QUESTION_ID?.trim()
  || '27c405e5-f912-41f3-a2a4-9af00e6e923e';
const OPEN_QUESTION_ID = processProxy.VUE_APP_COMPARATOR_FEEDBACK_OPEN_QUESTION_ID?.trim()
  || 'b6065c80-e7b5-4844-978d-e04165ef225f';

const SURVEY_SENT_EVENT = 'survey sent';

// PostHog's per-event size limit hovers around 1 MB; we leave headroom for
// headers + property overhead so the request body never sits right against
// the edge (Vercel / our reverse proxies have their own buffer concerns
// at >900 KB too). When the serialized properties cross this threshold we
// drop the screenshot and re-send — the rating + comment still land, and
// the event is annotated so we can see in PostHog how often this fires.
const MAX_PAYLOAD_BYTES = 900_000;
const IS_DEV = processProxy.NODE_ENV !== 'production';

export type ComparatorFeedbackPayload = {
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string;
  screenshot?: string | null; // base64 data URL, may be omitted
  productType?: string | null;
  simulationId?: string | null;
  routeName?: string | null;
};

/**
 * Build the property object for a `survey sent` event. The `$survey_id`
 * and `$survey_response_<question_id>` keys are what PostHog's Surveys
 * product uses to attribute responses to the right survey + question —
 * any custom metadata lives alongside them as ordinary properties.
 */
const buildSurveyProperties = (payload: ComparatorFeedbackPayload): Record<string, unknown> => {
  const trimmedComment = payload.comment?.trim() ?? '';
  const properties: Record<string, unknown> = {
    // --- PostHog Survey response fields ---
    $survey_id: SURVEY_ID,
    $survey_name: SURVEY_NAME,
    // Rating question — PostHog stores rating responses as the numeric value
    // (1..5 for our 5-point emoji scale).
    [`$survey_response_${RATING_QUESTION_ID}`]: payload.rating,
    // Open-text question — empty string when the user submitted no comment;
    // PostHog still records it so we can compute response-rate per question.
    [`$survey_response_${OPEN_QUESTION_ID}`]: trimmedComment,

    // --- Non-survey metadata (visible in event details, not Survey
    // results view). Useful for cross-referencing with the simulation a
    // piece of feedback was given on. ---
    rating: payload.rating,
    hasComment: trimmedComment.length > 0,
    commentLength: trimmedComment.length,
    hasScreenshot: Boolean(payload.screenshot),
    productType: payload.productType ?? null,
    simulationId: payload.simulationId ?? null,
    routeName: payload.routeName ?? null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
    $origin_lib: 'comparator-feedback',
    $origin_lib_version: '3',
  };

  // Screenshot ships as a non-survey property — PostHog Surveys doesn't
  // support file attachments natively, so it lives outside `$survey_*` and
  // is reachable via the event details panel only.
  if (payload.screenshot) {
    properties.screenshot = payload.screenshot;
  }

  return properties;
};

/**
 * Fires the survey-response event through the posthog-js SDK. Always
 * resolves — never throws — so the UI's "Thanks" flow runs regardless of
 * config state.
 */
export const captureComparatorFeedback = async (payload: ComparatorFeedbackPayload): Promise<boolean> => {
  if (!hasPosthogApiKey()) {
    if (IS_DEV) console.info('[comparatorFeedback] PostHog API key missing — feedback not sent', payload);
    return false;
  }

  const properties = buildSurveyProperties(payload);

  // Properties too big — drop the screenshot rather than the whole event.
  // Tag the event so we can monitor in PostHog how often this fires (and
  // decide whether to scale screenshot quality down).
  if (properties.screenshot && JSON.stringify(properties).length > MAX_PAYLOAD_BYTES) {
    delete properties.screenshot;
    properties.hasScreenshot = false;
    properties.screenshotDropped = true;
    properties.screenshotDroppedReason = 'payload_too_large';
    if (IS_DEV) {
      console.warn(
        '[comparatorFeedback] payload exceeded',
        MAX_PAYLOAD_BYTES,
        'bytes — dropped screenshot, kept rating+comment',
      );
    }
  }

  posthog.capture(SURVEY_SENT_EVENT, properties);
  return true;
};
